/**
 * Portal view host (SPEC E4 R3 - AC2, AC7).
 *
 * The single host rendered under PortalLayout's <Outlet/>. It reads the CLIENT's
 * `portal_views` (the four-bool map from /api/me) and:
 *   - renders one nav tab per ENABLED view, in registry order (AC2);
 *   - renders the view selected by the `:view` URL param;
 *   - redirects an out-of-bounds / DISABLED view (or `/portal` with the first
 *     enabled view) so a disabled view is unreachable by direct URL (AC7).
 *
 * NO operator nav links appear (AC8) - only the enabled portal views.
 *
 * `enabled_views` (the registry list inlined on /api/me) is the AUTHORITATIVE
 * order/source; `portal_views` is the per-view bool fallback for older payloads.
 */

import { Navigate, NavLink, useParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import {
  FEATURE_REGISTRY,
  getFeature,
  resolveEnabledFeatures,
} from '@/lib/app-template'

// SPEC PX-11 R2 (AC7): the view registry + components are GENERALIZED into the
// template-driven `FEATURE_REGISTRY` (frontend/src/lib/app-template/). A tenant
// app composes the EXISTING Portal* components from that ONE registry, driven by
// the template's enabled-views manifest (user.enabled_views). Adding a shared
// feature is ONE registry entry there (plus the backend PORTAL_VIEW_REGISTRY),
// not a per-tenant fork.

function useEnabledViews(): string[] {
  const { user } = useAuth()
  // Prefer the explicit registry-ordered enabled_views list; fall back to the
  // four-bool portal_views map. Resolution lives in the shared registry.
  // portal_views is a typed PortalViews struct; the resolver reads it as a
  // string-keyed bool map (the prior inline cast, preserved).
  const bools = user?.portal_views as Record<string, boolean> | null | undefined
  return resolveEnabledFeatures(user?.enabled_views, bools)
}

export function PortalView() {
  const { view } = useParams<{ view?: string }>()
  const enabled = useEnabledViews()

  // No views enabled → calm empty state (a tenant configured with zero views,
  // or an unconfigured tenant - the safe all-off default).
  if (enabled.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed p-8 text-center"
        data-testid="portal-no-views"
      >
        <p className="text-sm font-medium text-foreground">
          Your portal is being set up
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Check back soon - your views will appear here.
        </p>
      </div>
    )
  }

  // `/portal` (no :view) → first enabled view. A `:view` not enabled (disabled or
  // unknown) → redirect to the first enabled view (AC7 - disabled view
  // unreachable by direct URL).
  if (!view) {
    return <Navigate to={`/portal/${enabled[0]}`} replace />
  }
  if (!enabled.includes(view)) {
    return <Navigate to={`/portal/${enabled[0]}`} replace />
  }

  const ViewComponent = getFeature(view)?.component

  return (
    <div>
      <nav
        // SPEC FE-1 / R3a: scroll the tab bar horizontally on narrow viewports
        // instead of overflowing (flex-nowrap + overflow-x-auto; tabs shrink-0).
        className="mb-6 flex flex-nowrap gap-1 overflow-x-auto border-b"
        data-testid="portal-tabs"
        aria-label="Portal views"
      >
        {enabled.map((key) => {
          const label =
            FEATURE_REGISTRY.find((v) => v.key === key)?.label ?? key
          return (
            <NavLink
              key={key}
              to={`/portal/${key}`}
              className={({ isActive }) =>
                cn(
                  'relative shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )
              }
            >
              {({ isActive }) => (
                <>
                  {label}
                  {/* The active-tab gradient indicator (2 of the <=2 sparing gradient slots) */}
                  {isActive && (
                    <span className="absolute inset-x-0 -bottom-px h-0.5 bg-gradient-to-r from-primary to-[#7C3AED]" />
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>
      <section data-testid={`portal-view-${view}`}>
        {ViewComponent ? <ViewComponent /> : null}
      </section>
    </div>
  )
}
